// src/components/repasses/hooks/handleCompareRepasse.ts (Versão Definitiva com Todas as Correções)

import { RowDataRepasse, CommissionDetail } from "../types";

// CORREÇÃO CRÍTICA: Ajustado para que 1 centavo (0.01) seja considerado divergência.
const VALUE_TOLERANCE = 0.0099;

const parseValue = (valueStr: string | undefined | number): number => {
	if (valueStr == null) return 0;
	const strValue = String(valueStr);
	const cleaned = strValue.replace(",", ".").replace(/[^\d.]/g, "");
	return parseFloat(cleaned) || 0;
};

// Estrutura para facilitar a busca e a soma dos detalhes da API
interface ApiDetailSummary {
	details: CommissionDetail[];
	totalValue: number;
	isCanceled: boolean;
	// NOVO: Armazena todos os tipos válidos para este certificado, para a mensagem de erro
	validTypes: Set<string>;
}

export const handleCompareRepasse = (processedData: RowDataRepasse[]): { updatedData: RowDataRepasse[]; newApiItems: RowDataRepasse[] } => {
	// Mapa: Certificado_Tipo -> ApiDetailSummary
	const apiSummaryMap = new Map<string, ApiDetailSummary>();
	const uniqueApiDetails = new Map<string, CommissionDetail>();
	const matchedApiItems = new Set<string>();

	// 1. Popula o Mapa de Detalhes da API de forma única e agregada
	for (const row of processedData) {
		if (row.commissionDetails && Array.isArray(row.commissionDetails)) {
			for (const detail of row.commissionDetails) {
				uniqueApiDetails.set(detail.id, detail);
			}
		}
	}

	// 1.2 Agrega os detalhes únicos por Certificado_Tipo
	for (const detail of Array.from(uniqueApiDetails.values())) {
		const certNum = detail.proposalCertificateNumber;
		const type = String(detail.type).toLowerCase().trim();
		const key = `${certNum}_${type}`;

		if (!apiSummaryMap.has(key)) {
			apiSummaryMap.set(key, { details: [], totalValue: 0, isCanceled: false, validTypes: new Set<string>() });
		}

		const summary = apiSummaryMap.get(key)!;
		summary.details.push(detail);
		summary.totalValue += parseValue(detail.value);
		summary.validTypes.add(type); // Adiciona o tipo válido

		if (detail.status && (detail.status.toLowerCase() === "canceled" || detail.status.toLowerCase() === "cancelado")) {
			summary.isCanceled = true;
		}
	}

	const updatedData: RowDataRepasse[] = [];

	// 2. Confere os Itens do CSV (Planilha)
	for (const row of processedData) {
		let currentStatus = row.status;
		let currentMensagem = row.mensagem;

		const certNum = parseValue(row.certificado);
		const csvValue = parseValue(row.valor);
		const csvType = String(row.tipo).toLowerCase().trim();
		const key = `${certNum}_${csvType}`; // Chave de busca para o summary

		// --- CONSUMO DE ITENS EM CASO DE ERRO DE CONTEXTO ---
		if (currentStatus === "inválido") {
			if (row.commissionDetails) {
				row.commissionDetails.forEach((detail) => matchedApiItems.add(detail.id));
			}
			updatedData.push(row);
			continue;
		}

		// --- CONFERÊNCIA DE VALORES/TIPOS ---

		const summary = apiSummaryMap.get(key);

		if (!summary) {
			// Verifica se o certificado existe com OUTRO TIPO
			const existingSummaries = Array.from(apiSummaryMap.keys()).filter((k) => k.startsWith(`${certNum}_`));

			if (existingSummaries.length > 0) {
				// Certificado existe, mas o Tipo não bate (ex: CSV='premio', API='bonus')
				const validTypes = Array.from(new Set(existingSummaries.map((k) => k.split("_")[1]))).join(", ");
				currentStatus = "inválido";
				currentMensagem = `Tipo '${row.tipo}' incorreto. Tipos válidos na API para o Certificado ${certNum}: ${validTypes}.`;
			} else {
				// O certificado (e, portanto, o tipo) não existe
				currentStatus = "inválido";
				currentMensagem = "Não encontrada no sistema.";
			}
		} else if (summary.isCanceled) {
			currentStatus = "inválido";
			currentMensagem = "**Cobrança cancelada**.";

			summary.details.forEach((detail) => matchedApiItems.add(detail.id));
		} else {
			// Se NÃO está cancelado, verifica Valor
			let divergences: string[] = [];

			// Comparação de Valores
			if (Math.abs(summary.totalValue - csvValue) > VALUE_TOLERANCE) {
				const diff = csvValue - summary.totalValue;
				const diffStr = diff.toFixed(2);

				if (parseFloat(diffStr) > 0) {
					divergences.push(`Valor do CSV R$${csvValue.toFixed(2)} é maior que R$${summary.totalValue.toFixed(2)} da API (Dif: R$${diffStr}).`);
				} else {
					divergences.push(`Valor do CSV R$${csvValue.toFixed(2)} é menor que R$${summary.totalValue.toFixed(2)} da API (Dif: R$${diffStr}).`);
				}
			}

			// Marca todos os IDs associados a este sumário como consumidos.
			summary.details.forEach((detail) => matchedApiItems.add(detail.id));

			// Atualização de Status/Mensagem
			if (divergences.length > 0) {
				currentStatus = "inválido";
				currentMensagem = divergences.join(" | ");
			} else {
				currentStatus = "validado";
				currentMensagem = "Dados conferidos com sucesso.";
			}
		}

		updatedData.push({
			...row,
			status: currentStatus,
			mensagem: currentMensagem,
		});
	}

	// 3. Identifica Itens da API Não Encontrados no CSV (Correção de Duplicação)
	const newApiItems: RowDataRepasse[] = [];

	// Itera sobre todos os detalhes ÚNICOS que encontramos na API
	for (const detail of Array.from(uniqueApiDetails.values())) {
		if (!matchedApiItems.has(detail.id)) {
			const isCanceled = detail.status && (detail.status.toLowerCase() === "canceled" || detail.status.toLowerCase() === "cancelado");

			if (!isCanceled) {
				newApiItems.push({
					RowNumber: -1,
					cnpjImobiliaria: detail.clientDocument,
					certificado: String(detail.proposalCertificateNumber),
					valor: detail.value,
					tipo: detail.type,
					vencimento: detail.dueDate ? detail.dueDate.split("T")[0] : "",
					idComissao: detail.clientId,
					commissionDetails: [detail],
					status: "inválido",
					mensagem: `Não encontrada na verificação (Certificado ${detail.proposalCertificateNumber}, Tipo ${detail.type}).`,
				} as RowDataRepasse);

				matchedApiItems.add(detail.id);
			}
		}
	}

	return { updatedData, newApiItems };
};
